/* eslint-disable ember/no-runloop, warp-drive/no-external-request-patterns, warp-drive/no-legacy-request-patterns */
// Ember imports
import Service, { service } from '@ember/service';
import { isArray } from '@ember/array';
import { isEmpty } from '@ember/utils';
import { later } from '@ember/runloop';
// Internal imports
import EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ReceiptFormat from '@linc-technologies/linced-common/enums/receipt-format';
import Scopes from '@linc-technologies/linced-common/enums/scopes';

// External imports
import ENV from '@linc-technologies/linced-common/utils/config';
import moment from 'moment';

const downloadAttrSupported = 'download' in document.createElement('a');

// NOTE: if any of these values are changed, you will have to make changes in the print service 😈
export const RECEIPT_HEADERS = [
  'Payment Date',
  'Ref',
  'Student',
  'Line Item',
  'Payment Details',
  'Amount',
];

export default class PrintService extends Service {
  // Services
  @service
  media;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  tax;

  @optionAlias('finance:school:showTaxOnStatements')
  showTaxOnStatements;

  // returns the corresponding receipt template filename
  _getReceiptTemplate(jurisdiction, format, isCommunity = false) {
    let receiptTemplate = 'receipt';
    switch (jurisdiction) {
      case Jurisdiction.NZ_MOE:
        break;
      case Jurisdiction.AU_NSW:
        if (isCommunity) {
          receiptTemplate += '_community';
        }
        receiptTemplate += '_nsw';

        // 80mm receipt format is only supported for NSW
        if (format === ReceiptFormat.EIGHTYMM) {
          receiptTemplate += '_80mm';
        }
        break;
      default:
        receiptTemplate += '_international';
    }

    return receiptTemplate + '.docx';
  }

  async receipt(
    transaction,
    accounts,
    school,
    format = ReceiptFormat.STANDARD,
    onComplete = null,
  ) {
    let account = await this._getEldestSiblingAccount(
      transaction.get('account'),
      accounts,
    );
    let jurisdiction = school.jurisdiction;
    let data = await this._generateReceiptData(
      [transaction],
      account,
      accounts,
      jurisdiction,
      account.person.get('jurisdictionalId'),
      format,
      true,
      true,
      school,
    );
    this.download(
      'fromTemplate',
      data,
      'receipt.docx',
      onComplete,
      this._getReceiptTemplate(jurisdiction, format),
      format,
      !this.permissions.meet(Scopes.TENANT_STAFF),
    );
  }

  async fullReceipt(
    transactions,
    accounts,
    year,
    school,
    format = ReceiptFormat.STANDARD,
    onComplete = null,
  ) {
    let eldestAccount = await this._getEldestSiblingAccount(
      accounts[0],
      accounts,
    );
    let jurisdiction = school.jurisdiction;
    let data = await this._generateReceiptData(
      transactions,
      eldestAccount,
      accounts,
      jurisdiction,
      eldestAccount.person.get('jurisdictionalId'),
      format,
      true,
      true,
      school,
    );
    let fileName = `Full Receipt - ${year}.docx`;
    this.download(
      'fromTemplate',
      data,
      fileName,
      onComplete,
      this._getReceiptTemplate(jurisdiction, format),
      format,
      true,
    );
  }

  async donationReceipt(
    transactions,
    accounts,
    taxYear,
    school,
    format = ReceiptFormat.STANDARD,
    onComplete,
  ) {
    let eldestAccount = await this._getEldestSiblingAccount(
      accounts[0],
      accounts,
    );
    let start = moment(taxYear.start).format('D MMMM YYYY');
    let end = moment(taxYear.end).format('D MMMM YYYY');
    let subheading = `Tax Receipt for ${start} - ${end}`;
    let jurisdiction = school.jurisdiction;
    let data = await this._generateDonationReceiptData(
      transactions,
      eldestAccount,
      accounts,
      jurisdiction,
      eldestAccount.person.get('jurisdictionalId'),
      true,
      false,
      school,
      subheading,
      format,
    );
    let fileName = `Donations Tax Receipt - ${taxYear.label}.docx`;
    this.download(
      'fromTemplate',
      data,
      fileName,
      onComplete,
      this._getReceiptTemplate(jurisdiction, format),
      format,
      true,
    );
  }

  // printText prints a simple text note
  printText(title, subheading, content, styles) {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let titleString = '';
    if (title) {
      titleString += `<h1>${title}</h1>`;
    }
    if (subheading) {
      titleString += `<h2>${subheading}</h2>`;
    }

    styles = `<style>
                body {
                    font-family: -apple-system, BlinkMacSystemFont, Roboto, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
                    }
                h1 {
                    text-align: center;
                    text-transform: uppercase;
                    font-size: 1em;
                    }
                ${styles}
              </style>`;

    content = `<div>${content}</div>`;
    printer.document.head.innerHTML = styles;
    printer.document.body.innerHTML = titleString + content;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }

  download(
    type,
    placeholders,
    fileName,
    onComplete,
    template,
    format = ReceiptFormat.STANDARD,
    protect = false,
  ) {
    let data = {};
    if (isArray(placeholders)) {
      placeholders.reduce((data, placeholder, index) => {
        data[index] = placeholder;
        return data;
      }, data);
    } else {
      data = placeholders;
    }
    data.format = ReceiptFormat.getLabel(format);
    data.report = type;
    if (!isEmpty(template)) {
      data.template = template;
    }
    if (protect) {
      // Enable DOCX protection.
      data.protected = 'true';
    }

    let req = new Request(ENV.APP.apiHost + '/print/v4/print', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    fetch(req)
      .then((response) => response.blob())
      .then((blob) => {
        let blobUrl = URL.createObjectURL(blob);

        if (downloadAttrSupported) {
          let link = this._getLink();
          link.href = blobUrl;
          if (!isEmpty(fileName)) {
            link.download = cleanFileName(fileName);
          } else {
            link.download = 'document.docx';
          }
          link.click();
        } else {
          let frame = this._getFrame();
          frame.src = blobUrl;
        }

        if (!isEmpty(onComplete)) {
          onComplete(true);
        }
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        onComplete(false);
      });
  }

  async _corePlaceholders(
    learner,
    includeLearnerName = true,
    includeHomeroomName = true,
    learnerLabel = 'Family of',
    school = null,
    format,
  ) {
    let placeholders = [];

    // Learner details
    if (includeLearnerName && !isEmpty(learner)) {
      placeholders.push({
        placeholder: 'STUDENTLABEL',
        value: learnerLabel,
        type: 'text',
      });
      placeholders.push({
        placeholder: 'STUDENT',
        value: learner.get('fullName'),
        type: 'text',
      });
    }
    if (includeHomeroomName && !isEmpty(learner)) {
      // Use the homeroom the person model resolves, rather than re-fetching by
      // learner.homeroomId. That id is not reliable here: ListPeople replaces a
      // non-current learner's sub-record with their enrolment's, so it can be
      // null (findRecord then threw) or point at the old school's group (the
      // wrong room was printed). The model already falls back to group
      // membership in both cases.
      //
      // It may resolve to a promise, and to null once resolved, so the emptiness
      // check has to happen after awaiting it.
      let homeroom = await learner.homeroom;
      if (!isEmpty(homeroom)) {
        placeholders.push({
          placeholder: 'ROOMLABEL',
          value: this.strings.homeroom,
          type: 'text',
        });
        placeholders.push({
          placeholder: 'ROOM',
          value: homeroom.label,
          type: 'text',
        });
      }
    }

    // Current date
    placeholders.push({
      placeholder: 'DATE',
      value: moment().format('YYYY-MM-DD'),
      type: 'text',
    });

    if (!isEmpty(this._getGSTNumber(school))) {
      placeholders.push({
        placeholder: 'GST',
        value: this._getGSTNumber(school),
        type: 'text',
      });
    }

    // School details
    // If no school was provided, default to user's current school.
    if (isEmpty(school)) {
      school = this.session.school;
    }
    placeholders.push({
      placeholder: 'COMPANYNAME',
      value: school.name,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'SCHOOL_NAME',
      value: school.name,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'SCHOOL_EMAIL',
      value: school.emailAddress,
      type: 'text',
    });

    let schoolAddress = school.postalAddress || school.physicalAddress;
    if (!isEmpty(schoolAddress)) {
      let schoolAddressLines = [
        `${schoolAddress.number} ${schoolAddress.street}`.trim(),
        schoolAddress.locality,
        schoolAddress.district,
        schoolAddress.postcode,
      ];
      placeholders.push({
        placeholder: 'SCHOOL_ADDRESS',
        value:
          '<span style="font-size:10pt; font-family:Arial, sans-serif' +
          '">' +
          schoolAddressLines.join('<br>') +
          '</span>',
        type: 'html',
      });
    }

    if (!isEmpty(school.logoMediaId)) {
      let logoUri = await this.media.getFileLocation(
        school.logoMediaId,
        'avatar',
      );
      let imgSize = format === ReceiptFormat.EIGHTYMM ? 80 : 110;
      placeholders.push({
        placeholder: 'LOGO',
        value: `<img src="${logoUri}" height=${imgSize}>`,
        type: 'html',
      });
    }

    return placeholders;
  }

  _reducePlaceholders(placeholders) {
    return placeholders.reduce((data, placeholder, index) => {
      data[index] = placeholder;
      return data;
    }, {});
  }

  _itemsTable(
    items,
    itemTotal,
    headers,
    jurisdiction = 0,
    format = ReceiptFormat.STANDARD,
    gstTotal,
  ) {
    items.unshift(headers);
    if (format === ReceiptFormat.STANDARD) {
      if (gstTotal) {
        items.push(['GST Total', '', '', '', '', '$' + gstTotal.toFixed(2)]);
      }
      items.push([
        'Total (inc GST)',
        '',
        '',
        '',
        '',
        '$' + itemTotal.toFixed(2),
      ]);
    }
    let paymentsTable = {
      placeholder: 'PAYMENTSTABLE',
      value: items,
      type: 'table',
    };
    if (jurisdiction === Jurisdiction.AU_NSW) {
      paymentsTable.format = ReceiptFormat.getLabel(format);
      paymentsTable.label = 'Fee Line Items';
    } else {
      paymentsTable.label = 'Non-Donation Line Items';
    }
    return paymentsTable;
  }

  _donationsTable(
    donations,
    donationTotal,
    headers,
    jurisdiction = 0,
    format = ReceiptFormat.STANDARD,
  ) {
    donations.unshift(headers);
    donations.push(['Total', '', '', '', '', '$' + donationTotal.toFixed(2)]);
    let donationsTable = {
      placeholder: 'DONATIONSPAYMENTSTABLE',
      value: donations,
      type: 'table',
    };
    if (jurisdiction === Jurisdiction.AU_NSW) {
      donationsTable.format = ReceiptFormat.getLabel(format);
      donationsTable.label = 'Deductible-Gift Line Items';
    } else {
      donationsTable.label = 'Donation Line Items';
    }
    return donationsTable;
  }

  _getPersonName(transaction, receiptFormat, jurisdiction) {
    let person = transaction.get('account.person');

    if (isEmpty(person)) {
      return '';
    }

    let personName = person.get('fullName');

    if (
      receiptFormat === ReceiptFormat.EIGHTYMM &&
      jurisdiction === Jurisdiction.AU_NSW
    ) {
      let jurisdictionalId = person.get('jurisdictionalId');
      let homeroom = person.get('homeroom.label');
      if (!isEmpty(jurisdictionalId)) {
        personName += ` (${jurisdictionalId})`;
      }
      if (!isEmpty(homeroom)) {
        personName += ` - ${homeroom}`;
      }
    }

    return personName;
  }

  _receiptTransaction(txn, paymentDetails, format, jurisdiction) {
    let row;
    let txnAmount = txn.displayAmount;
    let personName = this._getPersonName(txn, format, jurisdiction);

    if (!isEmpty(txn.line_item_id)) {
      let itemDetails = txn.get('lineItem.name');
      let description = txn.get('lineItem.description');
      if (!isEmpty(description)) {
        itemDetails += '\n' + description;
      }
      row = [
        txn.date,
        txn.id,
        personName,
        itemDetails,
        paymentDetails,
        '$' + txnAmount.toFixed(2),
      ];
    } else {
      txnAmount = txn.amount;
      row = [
        txn.date,
        txn.id,
        personName,
        'Payment in Advance',
        paymentDetails,
        '$' + txnAmount.toFixed(2),
      ];
    }

    return {
      row,
      amount: txnAmount,
    };
  }

  async _generateReceiptData(
    transactions,
    account,
    accounts,
    jurisdiction = 0,
    jurisdictionalId = '',
    format = ReceiptFormat.STANDARD,
    includeLearnerName = true,
    includeHomeroomName = true,
    school = null,
  ) {
    let learner = account.get('person');
    let placeholders = await this._corePlaceholders(
      learner,
      includeLearnerName,
      includeHomeroomName,
      'Family of',
      school,
      format,
    );

    let items = []; // Non-donation items
    let donations = [];
    let itemTotal = 0;
    let donationTotal = 0;
    let till = '';
    let receiptNo = '';
    let gstTotal = 0;
    let paymentMethod = '';
    let time = '';

    for (const transaction of transactions) {
      // Skip cancelled transactions
      if (transaction.get('isCancelled')) {
        continue;
      }

      // Avoid lexical declarations within the switch cases, to shut eslint up.
      let item, person, amount, piaDetails, row, piaRow;
      let isDonation = false;

      let paymentDetails = transaction.get('displayMethod');
      if (
        !isEmpty(transaction.get('paidBy')) &&
        !isEmpty(transaction.get('paidBy').trim())
      ) {
        paymentDetails += `\nPaid by: ${transaction.get('paidBy').trim()}`;
      }

      if (jurisdiction === Jurisdiction.AU_NSW) {
        // Get till code, receiptNo (transaction id) and payment method
        till = transaction.get('tillSession.till.code');
        receiptNo = transaction.get('id');
        paymentMethod = transaction.get('displayMethod');
        time = moment.unix(transaction.get('createTime')).format('HH:mm');
      }

      switch (transaction.get('type')) {
        case TransactionType.REFUND:
          item = 'Payment in Advance';
          person = transaction.get('account.person.fullName');
          amount = transaction.get('amount');
          if (!isEmpty(transaction.get('line_item_id'))) {
            item = transaction.get('lineItem.name');
            person = transaction.get('lineItem.account.person.fullName');
            amount = transaction.get('displayAmount');
            isDonation = transaction.get('lineItem.isDonation');
          }
          row = [
            transaction.get('date'),
            transaction.get('id'),
            person,
            item,
            paymentDetails,
            '$' + amount.toFixed(2),
          ];

          if (isDonation) {
            donations.push(row);
            donationTotal += amount;
          } else {
            items.push(row);
            itemTotal += amount;
          }
          break;

        case TransactionType.TRANSFER:
          if (isEmpty(transaction.get('childTransactions'))) {
            // Transfer between PIA (account) and a Line Item
            if (transaction.get('amount') < 0) {
              paymentDetails = 'Transferred from:\nPayment in Advance';
              piaDetails = `Transferred to:\n${transaction.get(
                'lineItem.name',
              )}`;
            } else {
              paymentDetails = 'Transferred to:\nPayment in Advance';
              piaDetails = `Transferred from:\n${transaction.get(
                'lineItem.name',
              )}`;
            }
            if (
              transaction.get('account.id') !==
              transaction.get('lineItem.account.id')
            ) {
              paymentDetails += ` (${transaction.get(
                'account.person.firstName',
              )})`;
            }
            row = [
              transaction.get('date'),
              transaction.get('id'),
              transaction.get('lineItem.account.person.fullName'),
              transaction.get('lineItem.name'),
              paymentDetails,
              '$' + transaction.get('displayAmount').toFixed(2),
            ];
            piaRow = [
              transaction.get('date'),
              transaction.get('id'),
              this._getPersonName(transaction, format, jurisdiction),
              'Payment in Advance',
              piaDetails,
              '$' + transaction.get('amount').toFixed(2),
            ];
            if (isDonation || transaction.get('lineItem.isDonation')) {
              donations.push(row);
              donationTotal += transaction.get('displayAmount');
              items.push(piaRow);
              itemTotal += transaction.get('amount');
            } else {
              // Both rows are together - remove date and ID from PIA row.
              piaRow[0] = '';
              piaRow[1] = '';
              items.push(row);
              items.push(piaRow);
            }
          } else {
            // Transfer between accounts.  List the source line; child transaction will be output later.
            row = [
              transaction.get('date'),
              transaction.get('id'),
              this._getPersonName(transaction, format, jurisdiction),
              'Payment in Advance',
              paymentDetails,
              '$' + transaction.get('amount').toFixed(2),
            ];
            items.push(row);
            itemTotal += transaction.get('amount');
          }
      }

      transaction.get('childTransactions').forEach((txn) => {
        let data = this._receiptTransaction(
          txn,
          paymentDetails,
          format,
          jurisdiction,
        );

        if (txn.get('lineItem.isDonation')) {
          donations.push(data.row);
          donationTotal += data.amount;
        } else {
          items.push(data.row);
          itemTotal += data.amount;
        }

        gstTotal += txn.get('taxAmount');
      });

      let isRefund = transaction.get('type') === TransactionType.REFUND;
      let isTransfer = transaction.get('type') === TransactionType.TRANSFER;
      if (!isRefund && !isTransfer && transaction.get('piaAmount') > 0) {
        items.push([
          transaction.get('date'),
          transaction.get('id'),
          this._getPersonName(transaction, format, jurisdiction),
          'Payment in Advance',
          paymentDetails,
          '$' + transaction.get('piaAmount').toFixed(2),
        ]);
        itemTotal += transaction.get('piaAmount');
      }
    }

    if (
      jurisdiction === Jurisdiction.AU_NSW &&
      format === ReceiptFormat.EIGHTYMM
    ) {
      let paidBy = 'Unknown Person';
      if (transactions.length === 1) {
        paidBy = transactions[0].paidBy;
      }

      placeholders.push({
        placeholder: 'PAID_BY',
        value: paidBy,
        type: 'text',
      });

      placeholders.push({
        placeholder: 'RECEIPT_NO',
        value: receiptNo,
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TIME',
        value: time,
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TILL',
        value: till,
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TOTAL',
        value: (itemTotal + donationTotal).toFixed(2),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'GST_TOTAL',
        value: gstTotal.toFixed(2),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'PAYMENT_METHOD',
        value: paymentMethod,
        type: 'text',
      });
    }

    placeholders.push({
      placeholder: 'JURISDICTIONAL_ID',
      value: jurisdictionalId,
      type: 'text',
    });

    placeholders.push({
      placeholder: 'BLURB',
      value: this._getReceiptBlurb(school),
      type: 'text',
    });

    let signature = await this._getElectronicSignature(school);
    if (!isEmpty(signature)) {
      placeholders.push({
        placeholder: 'ELECTRONICSIGNATURE',
        value: signature,
        type: 'html',
      });
    }

    placeholders.push(...this._parentAddressPlaceholders(account, accounts));

    if (!isEmpty(items)) {
      placeholders.push(
        this._itemsTable(
          items,
          itemTotal,
          RECEIPT_HEADERS,
          jurisdiction,
          format,
          gstTotal,
        ),
      );
    }

    if (!isEmpty(donations)) {
      placeholders.push(
        this._donationsTable(
          donations,
          donationTotal,
          RECEIPT_HEADERS,
          jurisdiction,
          format,
        ),
      );
    }

    let data = this._reducePlaceholders(placeholders);

    data.overridetemplatesymbol = true;

    return data;
  }

  async _generateDonationReceiptData(
    transactions,
    account,
    accounts,
    jurisdiction = 0,
    jurisdictionalId = '',
    includeLearnerName = true,
    includeHomeroomName = true,
    school = null,
    subheading = '',
    format = ReceiptFormat.STANDARD,
  ) {
    let learner = account.get('person');
    let placeholders = await this._corePlaceholders(
      learner,
      includeLearnerName,
      includeHomeroomName,
      'Family of',
      school,
    );

    let donations = [];
    let donationTotal = 0;

    transactions.forEach((txn) => {
      // Skip cancelled transactions
      if (txn.isCancelled) {
        return;
      }
      switch (txn.type) {
        case TransactionType.VOID:
        case TransactionType.WRITEOFF:
        case TransactionType.DISCOUNT___EXEMPTION:
          // Don't include Voids, Writeoffs or Discounts/Exemptions in Donation Receipts.
          return;
      }
      let paymentDetails = txn.displayMethod;
      if (!isEmpty(txn.paidBy) && !isEmpty(txn.paidBy.trim())) {
        paymentDetails += `\nPaid by: ${txn.paidBy.trim()}`;
      }

      let data = this._receiptTransaction(
        txn,
        paymentDetails,
        format,
        jurisdiction,
      );

      donations.push(data.row);
      donationTotal += data.amount;
    });

    placeholders.push({
      placeholder: 'JURISDICTIONAL_ID',
      value: jurisdictionalId,
      type: 'text',
    });

    placeholders.push({
      placeholder: 'BLURB',
      value: this._getReceiptBlurb(school),
      type: 'text',
    });

    if (!isEmpty(subheading)) {
      placeholders.push({
        placeholder: 'SUBHEADING',
        value: subheading,
        type: 'text',
      });
    }

    let signature = await this._getElectronicSignature(school);
    if (!isEmpty(signature)) {
      placeholders.push({
        placeholder: 'ELECTRONICSIGNATURE',
        value: signature,
        type: 'html',
      });
    }

    placeholders.push(...this._parentAddressPlaceholders(account, accounts));

    if (!isEmpty(donations)) {
      placeholders.push(
        this._donationsTable(
          donations,
          donationTotal,
          RECEIPT_HEADERS,
          jurisdiction,
        ),
      );
    }

    let data = this._reducePlaceholders(placeholders);

    data.overridetemplatesymbol = true;

    return data;
  }

  _getReceiptBlurb(school) {
    if (isEmpty(school)) {
      // Use current user's school (default).
      return this.options.value('finance:school:receiptBlurb');
    } else {
      // Extract receipt blurb from provided school.
      return this.options.schoolGet(school, 'finance:school:receiptBlurb');
    }
  }

  async _getElectronicSignature(school) {
    let mediaId = '';
    if (isEmpty(school)) {
      // Use current user's school (default).
      mediaId = this.options.value('finance:school:electronicSignature');
    } else {
      // Extract receipt blurb from provided school.
      mediaId = this.options.schoolGet(
        school,
        'finance:school:electronicSignature',
      );
    }

    if (!isEmpty(mediaId)) {
      let mediaUri = await this.media.getFileLocation(mediaId, 'avatar');
      return `<img src="${mediaUri}" height="105">`;
    }
  }

  _getEldestSiblingAccount(account, accounts) {
    // Use a safe path to find the eldest sibling for all apps.
    let people = accounts.map((a) => a.person);
    let schoolId = accounts[0].get('school.id');
    let atThisSchool = people.filter((person) => {
      return person.get('learner.schoolId') === schoolId;
    });

    if (!isEmpty(atThisSchool)) {
      // Find the eldest "active" (non-former) student, if there is one.
      let active = atThisSchool.filter((person) => {
        switch (person.get('learner.enrolmentStub.status')) {
          case EnrolmentStatus.FORMER:
          case EnrolmentStatus.ARCHIVED:
            return false;
        }
        return true;
      });
      if (isEmpty(active)) {
        // If there are no "active" students, fall back to all at this school.
        active = atThisSchool;
      }

      // Find and return the eldest student.
      let eldest = active.find((person) => {
        return person.get('learner.siblingPosition') === 1;
      });
      if (isEmpty(eldest)) {
        // We didn't find any students who claim to be the eldest.  Sort by date of birth and return the eldest.
        eldest = active.sort((a, b) =>
          a.dob > b.dob ? 1 : a.dob < b.dob ? -1 : 0,
        )[0];
      }

      if (!isEmpty(eldest)) {
        return Promise.resolve(
          accounts.find((a) => a.person_id === eldest.get('id')),
        );
      }
    }

    // No students currently at the school in question, or we couldn't find the
    // eldest; just return _an_ account.
    if (isEmpty(account)) {
      return Promise.resolve(accounts[0]);
    }
    return Promise.resolve(account);
  }

  getAccountSalutation(account, accounts) {
    let salutation = account.get('salutation');
    if (isEmpty(salutation)) {
      if (isEmpty(accounts)) {
        accounts = [account];
      }
      let surnames = [
        ...new Set(accounts.map((a) => a.get('person.lastName'))),
      ];
      salutation = `Parent/Caregiver(s) of ${surnames.join(' / ')} Family`;
    }
    return salutation;
  }

  _parentAddressPlaceholders(account, accounts) {
    let salutation = this.getAccountSalutation(account, accounts);
    let address = this._getAccountAddress(account);
    return [
      {
        placeholder: 'PARENT',
        value: salutation,
        type: 'text',
      },
      {
        placeholder: 'ADDRESS',
        value:
          '<span style="font-size:10pt; font-family:Arial, sans-serif">' +
          address.join('<br>') +
          '</span>',
        type: 'html',
      },
    ];
  }

  _getAccountAddress(account) {
    // Accounts only provide an addressPersonId to caregivers if it's either the learner
    // or the caregiver, so we can safely just check for the presence of an ID and a record.
    let personId = account.get('addressPersonId');
    if (!isEmpty(personId)) {
      // The learner and logged-in caregiver should both already be in ember-data.  Peek it.
      let person = this.store.peekRecord('person', personId);
      if (!isEmpty(person)) {
        return person.get('address');
      }
    }

    if (this.permissions.equal(Scopes.CAREGIVER)) {
      // Return an empty address.
      return [];
    }
    // Otherwise, return an address from the account. Skip empty lines ('' is
    // the Go zero value).
    return [
      account.addressLine1,
      account.addressLine2,
      account.addressLine3,
      account.addressLine4,
      account.addressPostCode,
    ].filter(Boolean);
  }

  _getGSTNumber(school) {
    if (isEmpty(school)) {
      // Use current user's school (default).
      return this.options.value('finance:school:gst');
    } else {
      // Extract GST Number from provided school.
      return this.options.schoolGet(school, 'finance:school:gst');
    }
  }

  _getFrame() {
    let frame = document.querySelector('iframe#print-downloader-frame');
    if (isEmpty(frame)) {
      frame = document.createElement('iframe');
      frame.id = 'print-downloader-frame';
      document.body.appendChild(frame);
    }

    return frame;
  }

  _getLink() {
    let link = document.querySelector('a#print-downloader-link');
    if (isEmpty(link)) {
      link = document.createElement('a');
      link.id = 'print-downloader-link';
      document.body.appendChild(link);
    }

    return link;
  }
}
